<template>
  <button
    ref="backToTop"
    id="back-to-top"
    @click="scrollToTop"
    class="fixed text-xl rounded-lg z-10 bottom-5 end-5 h-8 w-8 text-center bg-white backdrop-blur-3xl text-default-950 flex justify-center items-center transition-all duration-500 hover:bg-primary hover:text-white hover:rounded-lg opacity-0 pointer-events-none"
  >
    <ChevronUp class="h-4 w-4" />
  </button>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { ChevronUp } from 'lucide-vue-next'

const props = defineProps<{
  scrollTarget?: HTMLElement | null
}>()

const backToTop = ref<HTMLElement | null>(null)
const currentTarget = ref<HTMLElement | Window>(props.scrollTarget ?? window)

const scrollHandler = () => {
  const scrollTop =
    currentTarget.value === window
      ? document.documentElement.scrollTop || document.body.scrollTop
      : (currentTarget.value as HTMLElement).scrollTop

  if (!backToTop.value) return

  const isVisible = scrollTop > 500
  backToTop.value.classList.toggle('opacity-100', isVisible)
  backToTop.value.classList.toggle('opacity-0', !isVisible)
  backToTop.value.classList.toggle('pointer-events-none', !isVisible)
}

const attachListener = (target: HTMLElement | Window) => {
  target.addEventListener('scroll', scrollHandler)
}

const detachListener = (target: HTMLElement | Window) => {
  target.removeEventListener('scroll', scrollHandler)
}

onMounted(() => {
  attachListener(currentTarget.value)
})

onUnmounted(() => {
  detachListener(currentTarget.value)
})

// Watch for changes in scrollTarget prop i.e. when Layout changes (public windows and private container)
watch(
  () => props.scrollTarget,
  (newTarget, oldTarget) => {
    detachListener(oldTarget ?? window)
    currentTarget.value = newTarget ?? window
    attachListener(currentTarget.value)
  }
)

const scrollToTop = () => {
  const el = currentTarget.value
  if (el === window) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } else {
    (el as HTMLElement).scrollTo({ top: 0, behavior: 'smooth' })
  }
}
</script>
